import React, { useState, useMemo, useEffect, useCallback } from 'react';

import { Box, Flex, Table, Tbody, Td, useTheme } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { Collapse, TableContainer } from '@material-ui/core';
import { useQueryClient } from '@tanstack/react-query';
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { updateTaskAction, removeTaskAction } from 'actions/task';
import useLocalStorage from 'components/hooks/useLocalStorage';
import { format } from 'date-fns';
import produce from 'immer';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { getDueDate } from 'screens/oneOnOne/landingPage/LandingPageV2/DateHelpers';

import {
  OpenSidePanel,
  CheckOutlineCircle,
  InitiativesIconDualTone,
  InitiativesIconHover,
} from 'assets/assetsV1/iconComponents';

import { localStorageKeys } from 'constants/localStorageKeys';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { createOrUpdateTask } from 'services/task';
import { showTaskDetails } from 'services/taskDetails';

import { Avatar } from 'uiToolkitV1/Avatar/Avatar';
import { AvatarGroup } from 'uiToolkitV1/AvatarGroup/';
import { Button } from 'uiToolkitV1/Button';
import { TableBodyRow, TableHeaderRow } from 'uiToolkitV1/TableComponents';
import { TablePagination } from 'uiToolkitV1/TableComponents/Pagination';
import { TableHeaderCell } from 'uiToolkitV1/TableComponents/TableHeader';
import { TableHead } from 'uiToolkitV1/TableComponents/TableHeader/TableHead';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

export const StyledInitiativeTableBodyRow = styled(TableBodyRow)(({ theme }) => ({
  '& .action': {
    display: 'none',
  },
  '&:hover': {
    cursor: 'pointer',
    '& .info': {
      display: 'none',
    },
    '& .action': {
      display: 'flex',
    },
    'td:first-of-type p': {
      color: theme.colors.text['1565BC'],
    },
  },
}));

export const getFlexJustifyFromCellAlign = (align) => {
  switch (align) {
    case 'left':
      return 'flex-start';
    case 'center':
      return 'center';
    case 'right':
      return 'flex-end';
    default:
      return 'center';
  }
};

const defaultRowsPerPage = 10;

const StyledTableContainer = styled(TableContainer)`
  min-width: ${(props) => props.$tableContainerWidth}px;
`;

export const defaultRowsPerPageLSObject = {
  [localStorageKeys.rabbitInitiativeTableRowsPerPage]: defaultRowsPerPage,
};

function ExpandableAuditTable({
  tableData,
  cohortDetails,
  dateRange,
  onMarkClicked,
  display,
  filters,
  cohort,
  hideEditActions = false,
}) {
  const theme = useTheme();
  const [sorting, setSorting] = React.useState([]);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState({});
  const [tableContainerWidth, setTableContainerWidth] = useState(1000);
  const history = useHistory();

  const [rowsPerPageLS, setRowsPerPageLS] = useLocalStorage(
    localStorageKeys.rabbitInitiativeTableRowsPerPage,
    defaultRowsPerPageLSObject
  );

  const onParentRowClick = useCallback((data) => {
    // setAvailableClickThroughPageMetaData(data);
  }, []);

  const renderOwners = (ownersArray) =>
    ownersArray?.map((owner) => <Avatar name={owner?.displayName} src={owner?.image} />);

  useEffect(() => {
    table.setPageSize(rowsPerPageLS[localStorageKeys.rabbitInitiativeTableRowsPerPage] ?? defaultRowsPerPage);
  }, [rowsPerPageLS]);

  const showDetails = (e, taskId, type) => {
    if (e) e.stopPropagation();
    dispatch(
      showTaskDetails({
        taskIds: [taskId],
        onDelete: () => {
          dispatch(removeTaskAction(taskId));
        },
        onUpdate: (task) => {
          if (task) dispatch(updateTaskAction(task));
        },
        employee: {},
        type,
        history,
      })
    );

    //  sendTaskEvent({ pageType, eventName: TASK_EVENTS.openedSidePanel, referrer: 'MeshList' });
  };

  const handleViewDetails = (e, id, type) => {
    showDetails(e, id, type);
  };

  const onMarkComplete = (e, task) => {
    e?.preventDefault();
    e?.stopPropagation();
    task.completionDate = new Date();

    queryClient.setQueryData([REACT_QUERY_KEYS.InitiativeTableData, filters, cohort, dateRange], (oldData) => {
      const updatedDataArray = produce(oldData, (draft) =>
        draft.map((item) => {
          if (String(task.id) === String(item.id)) {
            const newItem = { ...item };
            newItem.status = 'COMPLETED';
            return newItem;
          }
          return item;
        })
      );
      return updatedDataArray;
    });
    queryClient.invalidateQueries([REACT_QUERY_KEYS.InitiativeStatsData]);
    createOrUpdateTask({ ...task, overrideToIds: false })
      .then((updatedTask) => {
        // because createdDate doesn't come from backend on update
        updatedTask.createdDate = task.createdDate;
      })
      .catch((err) => {
        queryClient.invalidateQueries([REACT_QUERY_KEYS.InitiativeTableData]);
        queryClient.invalidateQueries([REACT_QUERY_KEYS.InitiativeStatsData]);
        console.log(err, 'Error in marking complete');
        //  dispatch(showErrorSnackbar('Please add a name to save.'));
      })
      .finally(() => {});
  };

  const columns = useMemo(() => {
    const newColumns = [
      {
        header: 'Initiatives',
        accessorKey: 'text',
        id: '1',
        enableSorting: false,
        cell: (props) => {
          const data = props.getValue();
          const rowData = props?.row?.original;
          const isCompleted = rowData?.status === 'COMPLETED';
          return (
            <Flex alignItems="center">
              <Box className="info" flexDirection="row" alignItems="center" display="flex">
                <InitiativesIconDualTone fill="transparent" />
                <TextBodyMedium textDecoration={isCompleted ? 'line-through' : 'none'} marginLeft="12px">
                  {data}
                </TextBodyMedium>
              </Box>
              <Box className="action" flexDirection="row" alignItems="center" display="flex">
                <InitiativesIconHover fill="transparent" />
                <TextBodyMedium textDecoration={isCompleted ? 'line-through' : 'none'} marginLeft="12px">
                  {data}
                </TextBodyMedium>
              </Box>
            </Flex>
          );
        },
        width: '40rem',
        align: 'left',
      },
      {
        header: 'Parent',
        accessorKey: 'parentPosts',
        id: '2',
        enableSorting: true,
        cell: (props) => {
          const data = props.getValue();
          const parentName = data?.task?.[0]?.text;
          const parentType = data?.task?.[0]?.type;
          const rowData = props?.row?.original;
          const isCompleted = rowData?.status === 'COMPLETED';
          return (
            <Flex flexDirection="column" style={{ opacity: isCompleted ? '.3' : '1' }}>
              <TextBodyMedium>{parentName}</TextBodyMedium>
              {/* <TextLabelSmall fontSize={'12px'} fontWeight={400} color={theme.colors.text.g2}>
                {parentName ? 'Goals' : '-'}
              </TextLabelSmall> */}
            </Flex>
          );
        },
        width: '10rem',
        align: 'left',
      },
      {
        header: 'Owners',
        accessorKey: 'toIds',
        enableSorting: false,
        id: '3',
        cell: (props) => {
          const data = props.getValue();
          const rowData = props?.row?.original;
          const isCompleted = rowData?.status === 'COMPLETED';
          return (
            <Flex height="36px">
              <Box className="info" style={{ opacity: isCompleted ? '.3' : '1' }}>
                <AvatarGroup max={3}>{renderOwners(data)}</AvatarGroup>
              </Box>
              <Flex justify="flex-end" w="100%" className="action" height="36px">
                {}
              </Flex>
            </Flex>
          );
        },
        width: '10rem',
        align: 'left',
      },
      {
        header: 'Due',
        accessorKey: 'coloredDueDate',
        enableSorting: true,
        id: '4',
        cell: (props) => {
          const data = props.getValue();
          const finalDate = format(new Date(data?.timeStamp), 'd MMM');
          const rowData = props?.row?.original;
          const isCompleted = rowData?.status === 'COMPLETED';
          return (
            <Flex minWidth="250px">
              <Flex className="info" style={{ opacity: isCompleted ? '.3' : '1' }}>
                <Flex flexDirection="column">
                  {data?.timeStamp && (
                    <>
                      <TextBodyMedium
                        fontWeight="400"
                        fontSize="14px"
                        lineHeight="20px"
                        color={theme.colors.functional['333333']}
                      >
                        {getDueDate(data?.timeStamp)}
                      </TextBodyMedium>
                      <TextLabel
                        fontWeight="400"
                        fontSize="12px"
                        lineHeight="134.2%"
                        color={theme.colors.secondary['666666']}
                      >{`${format(new Date(data?.timeStamp), 'MMM d, y')}`}</TextLabel>
                    </>
                  )}
                </Flex>
              </Flex>
              <Flex justifyContent="flex-end" className="action" ml="auto">
                <Button
                  variant="rowButtonOutlined"
                  StartIcon={OpenSidePanel}
                  onClick={(e) => handleViewDetails(e, rowData.uuid, 'TASK')}
                  className="hovering"
                  style={{ marginRight: '8px' }}
                >
                  View Details
                </Button>
                {!isCompleted && !hideEditActions ? (
                  <Button
                    variant="rowButtonOutlined"
                    onClick={(e) => onMarkComplete(e, rowData)}
                    StartIcon={CheckOutlineCircle}
                  >
                    Mark Complete
                  </Button>
                ) : null}
              </Flex>
            </Flex>
          );
        },
        width: '20rem',
        align: 'left',
      },
    ];
    return newColumns;
  }, []);

  const table = useReactTable({
    data: tableData ?? [],
    columns,
    state: {
      sorting,
    },
    onExpandedChange: setExpanded,
    getSubRows: (row) => row.subRows,
    paginateExpandedRows: false,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: (row) => Boolean(row?.original?.managers?.value?.reporteeCount),
    debugTable: true,
  });

  const renderRowSubComponent = React.useCallback(({ row, rowProps, visibleColumns }) => {
    const employeeId = row.original.managers.uuid;
    const depth = 1;
    return (
      //   <SubGroupedByEmployeesTable
      //     employeeId={employeeId}
      //     row={row}
      //     rowProps={rowProps}
      //     visibleColumns={visibleColumns}
      //     depth={depth}
      //     setTableContainerWidth={setTableContainerWidth}
      //   />
      <div>s</div>
    );
  }, []);

  return (
    <div>
      <TableContainer style={{ overflow: 'auto' }}>
        <StyledTableContainer $tableContainerWidth={tableContainerWidth} style={{ overflow: 'visible' }}>
          <Table>
            <TableHead>
              {table.getHeaderGroups().map((headerGroup, index) => (
                <TableHeaderRow
                  key={headerGroup.id}
                  sx={{ backgroundColor: index === 1 ? theme.colors.neutral.EBEFF3 : 'inherit' }}
                >
                  {' '}
                  {headerGroup.headers.map((header) => {
                    const { width, fixedWidth, align, borderLeft, marginLeft } = header?.column?.columnDef ?? {};
                    const justify = getFlexJustifyFromCellAlign(align);
                    return (
                      <TableHeaderCell
                        size="small"
                        variant="head"
                        width={width}
                        fixedWidth={fixedWidth}
                        // minWidth={minWidth}
                        key={header.id}
                        colSpan={header.colSpan}
                        justify={justify}
                        borderLeft={borderLeft}
                        sx={{
                          padding: index === 1 ? '6px' : '8px 16px !important',
                          div: { justifyContent: justify },
                        }}
                      >
                        {header.isPlaceholder ? null : (
                          <div id="fandom" onClick={header.column.getToggleSortingHandler()}>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {{
                              asc: ' 🔼',
                              desc: ' 🔽',
                            }[header.column.getIsSorted()] ?? null}
                          </div>
                        )}
                      </TableHeaderCell>
                    );
                  })}
                </TableHeaderRow>
              ))}
            </TableHead>
            <Tbody>
              {table.getRowModel().rows.map((row) => {
                const isRowExpanded = row?.getIsExpanded();

                return (
                  <React.Fragment key={row.id}>
                    <StyledInitiativeTableBodyRow onClick={(e) => handleViewDetails(e, row?.original?.id, 'TASK')}>
                      {row.getVisibleCells().map((cell, index) => (
                        <Td key={cell.id} sx={{ position: 'relative', padding: '16px' }}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </Td>
                      ))}
                    </StyledInitiativeTableBodyRow>

                    {isRowExpanded && (
                      <TableBodyRow key={`${row.id}-expanded`}>
                        <Td
                          style={{
                            padding: 0,
                            border: 'unset',
                          }}
                          colSpan="10" // <- used to control width of sub-table
                          sx={{ position: 'relative', padding: '16px' }}
                        >
                          <Collapse in={isRowExpanded} timeout="0" unmountOnExit>
                            {renderRowSubComponent({ row })}
                          </Collapse>
                        </Td>
                      </TableBodyRow>
                    )}
                  </React.Fragment>
                );
              })}
            </Tbody>
          </Table>
        </StyledTableContainer>
      </TableContainer>
      <Flex sx={{ justifyContent: 'flex-end' }}>
        <TablePagination
          rowsPerPageOptions={[10, 20, 30]}
          colSpan={0}
          count={tableData?.length}
          rowsPerPage={rowsPerPageLS[localStorageKeys.rabbitInitiativeTableRowsPerPage] ?? defaultRowsPerPage}
          page={table.getState().pagination.pageIndex}
          SelectProps={{
            inputProps: { 'aria-label': 'rows per page' },
          }}
          onPageChange={(_, pageNum) => {
            table.setPageIndex(pageNum);
          }}
          handleChangeRowsPerPage={({ target }) => {
            table.setPageSize(Number(target.value));
            setRowsPerPageLS({
              ...rowsPerPageLS,
              [localStorageKeys.rabbitInitiativeTableRowsPerPage]: target.value,
            });
          }}
        />
      </Flex>
    </div>
  );
}

export default ExpandableAuditTable;
